import { lazy, type ComponentType } from 'react';
import { isStaleAssetError, reloadOnceForStaleAsset } from './deployVersionSync';

type ComponentModule<T> = { default: T };
type ImportFactory<T> = () => Promise<ComponentModule<T>>;

/**
 * `React.lazy` that survives transient dynamic-import failures and self-heals
 * stale-chunk deploys.
 *
 * Landing sections are code-split into hashed `/assets/*` chunks. After a
 * deploy, a client holding stale HTML requests an old chunk hash that no longer
 * exists and throws `ChunkLoadError` / "Failed to fetch dynamically imported
 * module" — the cluster seen on `/` in Sentry.
 *
 * On a failed import we:
 *  1. retry once (short backoff) to absorb a transient network blip, then
 *  2. if it still looks like a stale/missing chunk, trigger the one-shot hard
 *     reload from {@link reloadOnceForStaleAsset} (sessionStorage-guarded, so it
 *     can't loop) to pull the fresh bundle.
 *
 * Non-stale errors (a real throw inside the module) are rethrown immediately so
 * the error boundary still surfaces genuine bugs.
 */
export function lazyWithRetry<T extends ComponentType<unknown>>(
  factory: ImportFactory<T>,
  retries = 1,
  backoffMs = 300
) {
  return lazy(async () => {
    let lastError: unknown;
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        return await factory();
      } catch (error) {
        lastError = error;
        // Only retry / reload for stale-asset style failures; surface real bugs.
        if (!isStaleAssetError(error)) break;
        if (attempt < retries) {
          await new Promise(resolve => setTimeout(resolve, backoffMs * (attempt + 1)));
          continue;
        }
        // Retries exhausted on a stale chunk: force a single hard reload so the
        // browser drops the cached HTML/module graph and fetches the new deploy.
        reloadOnceForStaleAsset('lazyWithRetry');
      }
    }
    throw lastError;
  });
}
